import api from './api';
import { deballer, nouvelleCleLot } from './lotsApi';

/**
 * Paiement du partenaire d'un lot attribué (lot 058) : acomptes et décomptes.
 * Chemins et formes de réponse : `backend/app/api/v1/lot_settlements.py` et
 * `backend/app/services/lot_settlement_service.py`.
 *
 * Tout est un montant : le serveur refuse (403) qui ne voit pas l'argent. Les
 * routes rendent le JSON brut (comme `lotsApi`), `deballer` accepte aussi
 * l'enveloppe `{ success, data }`. Aucun calcul ici.
 *
 * Chaque écriture porte une clé d'idempotence dans son corps : l'appelant la
 * fournit (et la garde tant que la demande n'a pas abouti) ou, à défaut, une
 * clé neuve est générée.
 */

const BASE = '/lots';

/** Paramètres de requête sans valeur vide (un filtre vide ne part pas). */
const sansVides = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, valeur]) => valeur !== undefined && valeur !== null && valeur !== ''),
  );

/** URL de l'impression d'un décompte (le format part en paramètre `format`). */
export const urlImpressionDecompte = (lotId, settlementId) =>
  `${BASE}/${lotId}/settlements/${settlementId}/print`;

export const lotPaymentsAPI = {
  // --- Lecture ------------------------------------------------------------------
  /** Situation de tous les lots : `{ lots, partners, totals }` (filtres `status`, `transporter_id`). */
  async summary(params = {}) {
    return deballer(await api.get(`${BASE}/payments/summary`, { params: sansVides(params) }));
  },
  /** Un lot : `{ lot, situation, advances, settlements }`. */
  async payments(lotId, options = {}) {
    return deballer(await api.get(`${BASE}/${lotId}/payments`, options));
  },
  /** Camions et acomptes qu'un décompte peut porter : `{ lot, trucks, advances, defaults }`. */
  async eligible(lotId, settlementId) {
    return deballer(
      await api.get(`${BASE}/${lotId}/settlements/eligible`, {
        params: settlementId ? { settlement_id: settlementId } : undefined,
      }),
    );
  },
  async getSettlement(lotId, settlementId) {
    return deballer(await api.get(`${BASE}/${lotId}/settlements/${settlementId}`));
  },

  // --- Écriture -------------------------------------------------------------------
  /** Verse un acompte au partenaire : rend la situation du lot et `advance_id`. */
  async recordAdvance(lotId, corps, idempotencyKey) {
    return deballer(
      await api.post(`${BASE}/${lotId}/advances`, {
        ...corps,
        idempotency_key: idempotencyKey || nouvelleCleLot('lot-acompte'),
      }),
    );
  },
  async createSettlement(lotId, corps, idempotencyKey) {
    return deballer(
      await api.post(`${BASE}/${lotId}/settlements`, {
        ...corps,
        idempotency_key: idempotencyKey || nouvelleCleLot('lot-decompte'),
      }),
    );
  },
  async updateSettlement(lotId, settlementId, corps, idempotencyKey) {
    return deballer(
      await api.patch(`${BASE}/${lotId}/settlements/${settlementId}`, {
        ...corps,
        idempotency_key: idempotencyKey || nouvelleCleLot('lot-decompte'),
      }),
    );
  },
  async validateSettlement(lotId, settlementId, idempotencyKey) {
    return deballer(
      await api.post(`${BASE}/${lotId}/settlements/${settlementId}/validate`, {
        idempotency_key: idempotencyKey || nouvelleCleLot('lot-decompte-valider'),
      }),
    );
  },
  /**
   * Paie tout ou partie du reste d'un décompte validé (au plus ce reste) : un
   * paiement au partenaire affecté à la facture du décompte. Rend le décompte
   * relu et `payment_id`.
   */
  async paySettlement(lotId, settlementId, corps, idempotencyKey) {
    return deballer(
      await api.post(`${BASE}/${lotId}/settlements/${settlementId}/payments`, {
        ...corps,
        idempotency_key: idempotencyKey || nouvelleCleLot('lot-decompte-paiement'),
      }),
    );
  },
  /** Le motif est obligatoire pour annuler un décompte validé. */
  async cancelSettlement(lotId, settlementId, { idempotencyKey, reason } = {}) {
    return deballer(
      await api.post(`${BASE}/${lotId}/settlements/${settlementId}/cancel`, {
        idempotency_key: idempotencyKey || nouvelleCleLot('lot-decompte-annuler'),
        reason: reason || null,
      }),
    );
  },
  /**
   * Supprime un décompte annulé (le serveur refuse un brouillon ou un décompte
   * validé). Sans clé : rejouer une suppression faite réussit sans effet.
   */
  async deleteSettlement(lotId, settlementId) {
    return deballer(await api.delete(`${BASE}/${lotId}/settlements/${settlementId}`));
  },
};

export default lotPaymentsAPI;
